import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import API_URL from '../config/api';

const Auctions = () => {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, active, upcoming, ended
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchAuctions = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auctions`);
        if (!response.ok) throw new Error('Chyba při načítání aukcí');
        const data = await response.json();
        setAuctions(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAuctions();
  }, []);

  const filteredAuctions = auctions.filter(auction => {
    switch(filter) {
      case 'active':
        return auction.status === 'live';
      case 'upcoming':
        return auction.status === 'registration';
      case 'ended':
        return auction.status === 'ended';
      default:
        return true;
    }
  });

  if (loading) return (
    <div className="min-h-screen bg-gray-900 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="animate-pulse text-white">Načítání aukcí...</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 py-6 sm:py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8">Dostupné aukce</h1>

        {/* Filtry - scrollovatelné na mobilu */}
        <div className="overflow-x-auto -mx-4 px-4 mb-6 sm:mb-8">
          <div className="flex space-x-2 sm:space-x-4 min-w-max">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base transition-all duration-300 ${
                filter === 'all' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Všechny
            </button>
            <button
              onClick={() => setFilter('active')}
              className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base transition-all duration-300 ${
                filter === 'active' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Probíhající
            </button>
            <button
              onClick={() => setFilter('upcoming')}
              className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base transition-all duration-300 ${
                filter === 'upcoming' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Připravované
            </button>
            <button
              onClick={() => setFilter('ended')}
              className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base transition-all duration-300 ${
                filter === 'ended' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Ukončené
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filteredAuctions.map(auction => (
            <Link 
              key={auction._id} 
              to={`/car/${auction.carId._id}`}
              className="bg-gray-800 rounded-lg overflow-hidden hover:transform hover:scale-105 
                       transition-all duration-300 shadow-lg hover:shadow-blue-500/20"
            >
              <div className="relative aspect-[16/9]">
                <img
                  src={auction.carId.images[0]}
                  alt={`${auction.carId.make} ${auction.carId.model}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className={`absolute top-2 right-2 sm:top-4 sm:right-4 px-2 sm:px-3 py-1 rounded-full 
                                text-xs sm:text-sm font-medium ${
                  auction.status === 'live' 
                    ? 'bg-green-600 text-white' 
                    : auction.status === 'registration' 
                    ? 'bg-purple-600 text-white'
                    : 'bg-red-600 text-white'
                }`}>
                  {auction.status === 'live' 
                    ? 'Probíhá' 
                    : auction.status === 'registration' 
                    ? 'Připravuje se'
                    : 'Ukončeno'}
                </div>
              </div>

              <div className="p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-bold text-white mb-2">
                  {auction.carId.make} {auction.carId.model}
                </h2>
                <div className="space-y-1 sm:space-y-2 text-sm sm:text-base text-gray-300">
                  <p>Rok: {auction.carId.year}</p>
                  <p>Najeto: {auction.carId.mileage.toLocaleString()} km</p>
                  <p>Motor: {auction.carId.engineCapacity} ccm</p>
                  <p className="text-base sm:text-lg font-semibold text-green-400 mt-3 sm:mt-4">
                    Aktuální cena: {auction.currentBid.toLocaleString()} Kč
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filteredAuctions.length === 0 && (
          <div className="text-center py-8 sm:py-12">
            <p className="text-gray-400">Žádné aukce nebyly nalezeny</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Auctions; 